/** Client-veilige prijs- en formatteringshelpers. */

export type Quote = {
  kind: "fixed" | "quote";
  routeId: string | null;
  fromCity: { id: string; name: string; slug: string; kind: string } | null;
  toCity: { id: string; name: string; slug: string; kind: string } | null;
  fromLabel: string;
  toLabel: string;
  /** Exacte prijs voor deze route (boeking). */
  customerPrice: number | null;
  /**
   * `true` wanneer de prijs uit de centrale tarieftabel komt en dus exact
   * gelijk is aan de calculator en de checkout. Alleen dan mag een pagina het
   * bedrag tonen; anders hoort er een oproep te staan om de prijs te berekenen.
   */
  exact?: boolean;
  driverPrice: number | null;
  rideType: string;
  pricingModel: string;
  distanceKm: number | null;
  durationMin: number | null;
  message?: string;
};

export const euro = (value: number | null | undefined) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(value);

/**
 * Kalenderdatum (YYYY-MM-DD) opmaken, onafhankelijk van de tijdzone van het
 * apparaat. Altijd in UTC formatteren, anders schuift de dag bij bezoekers
 * west van UTC (VS, Canada) één dag terug.
 */
export const formatCalendarDate = (iso: string, locale: "nl" | "en" = "nl") => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

export const dateNL = (iso: string) => formatCalendarDate(iso, "nl");

export const timeNL = (value: string) => value.slice(0, 5);

export const routePath = (fromSlug: string, toSlug: string) => `/taxi-${fromSlug}-${toSlug}`;
export const cityPath = (slug: string) => `/taxi-${slug}`;

export const STATUS_LABELS: Record<string, string> = {
  draft: "Concept",
  payment_pending: "Betaling in behandeling",
  paid: "Betaald",
  available: "Beschikbaar",
  assigned: "Aangenomen",
  driver_en_route: "Chauffeur onderweg",
  passenger_onboard: "Passagier opgehaald",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

export const PAYMENT_LABELS: Record<string, string> = {
  pending: "In afwachting",
  paid: "Betaald",
  failed: "Mislukt",
  refund_requested: "Terugbetaling aangevraagd",
  refunded: "Terugbetaald",
  cancelled: "Geannuleerd",
};

export const PRICING_MODEL_LABELS: Record<string, string> = {
  fixed: "1 – Vaste prijs",
  fixed_both: "2 – Vaste klant- + chauffeursprijs",
  dynamic_driver: "3 – Vaste klantprijs + dynamisch chauffeurstarief",
  manual_assign: "4 – Handmatige chauffeurstoewijzing",
  quote: "5 – Offerte aanvragen",
};

export const directionFor = (fromKind?: string | null, toKind?: string | null) =>
  toKind === "airport"
    ? "city_to_airport"
    : fromKind === "airport"
      ? "airport_to_city"
      : "city_to_city";
